/**
 * PKCE (RFC 7636) helpers for the Cognito Authorization Code flow.
 *
 * Why PKCE: SPAs cannot keep a client secret. PKCE binds the auth-code
 * issuance to the same browser tab via a one-time random verifier so an
 * intercepted code is useless without it.
 */

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/** 64-byte random string, base64url-encoded. RFC 7636 requires 43–128 chars. */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(64)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

/** SHA-256(verifier), base64url-encoded — the value sent to the auth server. */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(new Uint8Array(hash))
}

/** Random opaque string for the OAuth `state` param (CSRF guard). */
export function generateState(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}
